import type { CSSProperties, KeyboardEvent, MouseEvent, TouchEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-is-mobile";
import styles from "./confirm-modal.module.css";

type ConfirmModalProps = {
  onConfirm: () => void;
  onCancel: () => void;
};

const DISMISS_THRESHOLD_PX = 100;

export function ConfirmModal({ onConfirm, onCancel }: Readonly<ConfirmModalProps>) {
  const isMobile = useIsMobile();
  const startYRef = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleOverlayKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  const stopMouse = (e: MouseEvent<HTMLDivElement>) => e.stopPropagation();
  const stopKey = (e: KeyboardEvent<HTMLDivElement>) => e.stopPropagation();

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    startYRef.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!isMobile || startYRef.current === null) return;
    const delta = e.touches[0].clientY - startYRef.current;
    if (delta > 0) setDragOffset(delta);
  };

  const handleTouchEnd = () => {
    if (!isMobile || startYRef.current === null) return;
    const shouldDismiss = dragOffset > DISMISS_THRESHOLD_PX;
    startYRef.current = null;
    setDragOffset(0);
    if (shouldDismiss) onCancel();
  };

  const modalStyle: CSSProperties | undefined =
    isMobile && dragOffset > 0
      ? { transform: `translateY(${dragOffset}px)`, transition: "none" }
      : undefined;

  return (
    <div
      className={styles.overlay}
      data-testid="confirm-modal"
      onClick={onCancel}
      onKeyDown={handleOverlayKeyDown}
      role="presentation"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        tabIndex={-1}
        onClick={stopMouse}
        onKeyDown={stopKey}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={modalStyle}
      >
        <span className={styles.handle} aria-hidden="true" />
        <div className={styles.titleRow}>
          <h2 id="confirm-modal-title" className={styles.title}>Deseja remover esse veículo dos Favoritos?</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
            aria-label="Fechar"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="#2E2E3A"/>
            </svg>
          </button>
        </div>

        <p className={styles.body}>O veículo deixará de aparecer na sua lista.</p>

        <div className={styles.footer}>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onCancel}
              data-testid="confirm-modal-cancel"
            >
              Não
            </button>
            <button
              type="button"
              className={styles.confirmButton}
              onClick={onConfirm}
              data-testid="confirm-modal-confirm"
            >
              Sim
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
